import { cn } from "@/lib/utils";
import { RefreshCw } from "lucide-react";

// Enhanced Skeleton with shimmer effect
function Skeleton({ className, shimmer = true, ...props }) {
  return (
    <div
      className={cn(
        "rounded-md bg-gray-200/80 relative overflow-hidden",
        shimmer && "before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.5s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent",
        className
      )}
      {...props}
    />
  );
}

// Background refresh indicator - shows when fetching fresh data while displaying cache
function BackgroundRefreshIndicator({ isRefetching, className }) {
  if (!isRefetching) return null;
  
  return (
    <div 
      className={cn(
        "fixed top-20 right-4 z-50 flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-full shadow-lg border border-gray-200 animate-fade-in",
        className
      )}
    >
      <RefreshCw className="w-4 h-4 text-brand-orange-500 animate-spin" />
      <span className="text-xs text-gray-600 font-medium">Updating...</span>
    </div>
  );
}

// Fade transition wrapper for smooth data updates
function FadeTransition({ children, className }) {
  return (
    <div className={cn("animate-fade-in", className)}>
      {children}
    </div>
  );
}

// Card skeleton with enhanced animation
function CardSkeleton({ className, lines = 3 }) {
  return (
    <div className={cn("bg-white rounded-xl border border-gray-200 p-4 space-y-3", className)}>
      <Skeleton className="h-5 w-1/3" />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          className="h-4" 
          style={{ 
            width: `${85 - (i * 15)}%`,
            animationDelay: `${i * 0.1}s`
          }} 
        />
      ))}
    </div>
  );
}

// Stats card skeleton
function StatsCardSkeleton({ className }) {
  return (
    <div className={cn("bg-white rounded-xl border border-gray-200 p-6", className)}>
      <div className="flex items-center justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-12 w-12 rounded-xl" />
      </div>
    </div>
  );
}

// Driver card skeleton
function DriverCardSkeleton({ className }) {
  return (
    <div className={cn("bg-white rounded-xl border border-gray-200 p-5 space-y-3", className)}>
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-16 rounded-full" />
        </div>
        <Skeleton className="h-8 w-20 rounded-full" />
      </div>
      <div className="space-y-2 pt-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-36" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
    </div>
  );
}

// Job card skeleton
function JobCardSkeleton({ className }) {
  return (
    <div className={cn("bg-white rounded-xl border border-gray-200 p-4", className)}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-20" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    </div>
  );
}

// Call card skeleton for Booked Calls page
function CallCardSkeleton({ className }) {
  return (
    <div className={cn("bg-white rounded-xl border-l-4 border-l-green-500 border border-gray-200 p-4", className)}>
      <div className="flex items-start justify-between mb-3">
        <div className="space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-28" />
        </div>
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>
      <Skeleton className="h-10 w-full rounded-lg mb-3" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-12 w-full" />
      </div>
    </div>
  );
}

export { 
  Skeleton, 
  BackgroundRefreshIndicator, 
  FadeTransition,
  CardSkeleton,
  StatsCardSkeleton,
  DriverCardSkeleton,
  JobCardSkeleton,
  CallCardSkeleton
};
